// File: app/src/utils/chartScale.js
//
// Axis helpers for the hand-drawn SVG charts (issue #91).
//
// Pure: no React, no I/O.

/** A step of 1, 2, 2.5 or 5 × 10ⁿ giving about `count` ticks across the range. */
export const niceStep = (span, count = 5) => {
  if (!(span > 0)) return 1;
  const raw = span / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const unit = raw / mag;
  const nice = unit <= 1 ? 1 : unit <= 2 ? 2 : unit <= 2.5 ? 2.5 : unit <= 5 ? 5 : 10;
  return nice * mag;
};

/** A domain widened to whole steps, and the ticks inside it. */
export const niceDomain = (lo, hi, count = 5) => {
  if (lo === hi) {
    lo -= 1;
    hi += 1;
  }
  const step = niceStep(hi - lo, count);
  const start = Math.floor(lo / step) * step;
  const end = Math.ceil(hi / step) * step;
  const ticks = [];
  for (let v = start; v <= end + step / 2; v += step) ticks.push(Math.round(v * 1000) / 1000);
  return { lo: start, hi: end, step, ticks };
};

/** Duration ticks (seconds) a log axis can label without crowding. */
const DURATION_TICKS = [10, 30, 60, 120, 300, 600, 1200, 3600, 7200];

export const durationTicks = (lo, hi) => DURATION_TICKS.filter((t) => t >= lo && t <= hi);

/** "10 s", "1 min", "20 min", "1 h". */
export const durationLabel = (sec) => {
  if (sec < 60) return `${sec} s`;
  if (sec < 3600) return `${sec / 60} min`;
  return `${sec / 3600} h`;
};

// Steps a clock reads cleanly: 5 s up to an hour.
const TIME_STEPS = [5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600];

/** niceDomain for seconds, stepping in whole clock units (no 1:40 steps). */
export const niceTimeDomain = (lo, hi, count = 5) => {
  if (lo === hi) {
    lo -= 5;
    hi += 5;
  }
  const raw = (hi - lo) / count;
  const step = TIME_STEPS.find((s) => s >= raw) || Math.ceil(raw / 3600) * 3600;
  const start = Math.max(0, Math.floor(lo / step) * step);
  const end = Math.ceil(hi / step) * step;
  const ticks = [];
  for (let v = start; v <= end; v += step) ticks.push(v);
  return { lo: start, hi: end, step, ticks };
};
